import React, { useState, useRef, useEffect } from 'react';
import './StudentProjectMonitor.css';
import { FaVideo, FaPlay, FaTimes, FaLaptopCode, FaBook, FaLightbulb, FaUsers, FaEye } from 'react-icons/fa';

const StudentProjectMonitor = () => {
  const [isWebcamActive, setIsWebcamActive] = useState(false);
  const [isPredicting, setIsPredicting] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const isPredictingRef = useRef(false);

  const students = [
    {
      id: 1,
      name: "Emel Sude",
      image: "/emel-sude.jpg",
      description: "14 yaşında, teknolojiye meraklı bir öğrenci. Python ile projeler geliştiriyor ve yapay zekaya büyük ilgi duyuyor.",
      skills: [
        { name: "Python", icon: <FaLaptopCode /> },
        { name: "Temel Yapay Zeka", icon: <FaBook /> },
        { name: "Proje Geliştirme", icon: <FaLightbulb /> }
      ]
    },
    {
      id: 2,
      name: "Talha Kayra",
      image: "/talha-kayra.jpg",
      description: "14 yaşında yazılıma ilgi duyan bir genç. Yapay Zeka API kullanımı konusunda kendini geliştiriyor.",
      skills: [
        { name: "HTML & CSS", icon: <FaLaptopCode /> },
        { name: "API", icon: <FaBook /> },
        { name: "Python ile Oyun Yapımı", icon: <FaLightbulb /> }
      ]
    },
    {
      id: 3,
      name: "Emir",
      image: "/emir.jpg",
      description: "15 yaşında yapay zekaya, web geliştirmeye ve kodlamaya ilgi duyan bir öğrenci. Web geliştirme alanın da çok istekli.",
      skills: [
        { name: "HTML & CSS", icon: <FaLaptopCode /> },
        { name: "Python", icon: <FaBook /> },
        { name: "Python Yapay Zeka", icon: <FaLightbulb /> }
      ]
    }
  ];
  

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Video yüklendiğinde canvas boyutunu video boyutuyla eşleştir
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleLoadedMetadata = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }
    };
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [isWebcamActive]);

  const toggleWebcam = async () => {
    if (!isWebcamActive) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsWebcamActive(true);
      } catch (error) {
        console.error("Webcam erişiminde hata oluştu:", error);
        alert("Kamera erişimine izin vermeniz gerekiyor!");
      }
    } else {
      // Webcam kapatılırken akışı durdur ve tahmin döngüsünü sonlandır
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      videoRef.current.srcObject = null;
      setIsWebcamActive(false);
      setIsPredicting(false);
      isPredictingRef.current = false;
      clearCanvas();
    }
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(selectedStudent?.id === student.id ? null : student);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Kamera görüntüsünden bir kare yakalar ve base64 formatında döndürür
  const captureFrameAsBase64 = () => {
    const video = videoRef.current;
    if (!video) return null;
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = video.videoWidth;
    offscreenCanvas.height = video.videoHeight;
    const ctx = offscreenCanvas.getContext('2d');
    ctx.drawImage(video, 0, 0, offscreenCanvas.width, offscreenCanvas.height);
    return offscreenCanvas.toDataURL('image/jpeg');
  };

  // API'ye gönderip tahmin sonuçlarını alır
  const captureAndPredict = async () => {
    const base64Image = captureFrameAsBase64();
    if (!base64Image) return;
    try {
      const response = await fetch('https://4453-172-83-13-4.ngrok-free.app/predict', {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: base64Image })
      });
      
      const predictionResults = await response.json();
      drawPredictions(predictionResults);
    } catch (error) {
      console.error("Tahmin yapılırken hata oluştu:", error);
    }
  };

  // Tahmin sonuçlarını canvas üzerine çizdirir
  const drawPredictions = (predictions) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    predictions.forEach(prediction => {
      const [x1, y1, x2, y2] = prediction.box;
      ctx.strokeStyle = '#FB8500';
      ctx.lineWidth = 3;
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);

      // Kutu için dikdörtgen çiz
      ctx.fillStyle = 'rgba(251, 133, 0, 0.7)';
      ctx.fillRect(x1, y1 - 30, 150, 30);

      const labelText = `${prediction.label} (${prediction.confidence})`;
      ctx.font = 'bold 16px Montserrat, sans-serif';
      ctx.fillStyle = 'white';
      ctx.fillText(labelText, x1 + 5, y1 - 10);
    });
  };

  // Sürekli tahmin yapacak döngü
  const predictLoop = async () => {
    if (!isPredictingRef.current) return;
    await captureAndPredict();
    // Yaklaşık 1 saniyede bir kare işleme alıyoruz
    setTimeout(predictLoop, 250);
  };

  const runProject = () => {
    if (!isPredicting) {
      setIsPredicting(true);
      isPredictingRef.current = true;
      predictLoop();
    }
  };

  return (
    <div className="spm-container">
      <div className="spm-header">
        <div className="spm-logo">
          <FaUsers className="spm-logo-icon" />
          <h1 className="spm-title">PP001 Türk İşaret Dili Algılama Mezuniyet Projesi</h1>
        </div>
      </div>
      
      <div className="spm-content">
        <div className={`spm-team-panel ${isWebcamActive ? 'spm-panel-active' : ''}`}>
          <div className="spm-panel-header">
            <FaUsers className="spm-panel-icon" />
            <h2 className="spm-panel-title">Proje Takımı</h2>
          </div>
          
          <div className="spm-team-list">
            {students.map((student) => (
              <div 
                key={student.id}
                className={`spm-team-member ${selectedStudent?.id === student.id ? 'spm-member-selected' : ''}`}
                onClick={() => handleStudentClick(student)}
              >
                <div className="spm-member-avatar">
                  <div className="spm-avatar-border">
                    <img src={student.image} alt={student.name} />
                  </div>
                </div>
                <div className="spm-member-info">
                  <span className="spm-member-name">{student.name}</span>
                  <div className="spm-member-skills-preview">
                    {student.skills.map((skill, idx) => (
                      <span key={idx} className="spm-skill-icon">{skill.icon}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="spm-monitor-panel">
          <div className="spm-webcam-container">
            <video 
              ref={videoRef} 
              className={`spm-webcam-feed ${isWebcamActive ? 'spm-feed-active' : ''}`} 
              autoPlay 
              playsInline
            />
            <canvas ref={canvasRef} className="spm-prediction-overlay" />

            {!isWebcamActive && (
              <div className="spm-placeholder">
                <div className="spm-placeholder-icon">
                  <FaVideo />
                </div>
                <p className="spm-placeholder-text">Kamerayı açmak için butona tıklayın</p>
              </div>
            )}

            {selectedStudent && (
              <div className="spm-student-detail">
                <div className="spm-detail-header">
                  <h3 className="spm-detail-name">{selectedStudent.name}</h3>
                  <button 
                    className="spm-close-button"
                    onClick={() => setSelectedStudent(null)}
                  >
                    <FaTimes />
                  </button>
                </div>
                
                <div className="spm-detail-content">
                  <div className="spm-detail-image">
                    <img src={selectedStudent.image} alt={selectedStudent.name} />
                  </div>
                  <p className="spm-description">{selectedStudent.description}</p>
                </div>
                
                <div className="spm-skills-container">
                  {selectedStudent.skills.map((skill, index) => (
                    <div key={index} className="spm-skill-tag">
                      <span className="spm-skill-icon">{skill.icon}</span>
                      <span className="spm-skill-name">{skill.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="spm-controls">
            <button 
              className={`spm-control-button spm-webcam-toggle ${isWebcamActive ? 'spm-button-active' : ''}`}
              onClick={toggleWebcam}
            >
              <FaVideo className="spm-button-icon" />
              <span className="spm-button-text">{isWebcamActive ? 'Kamerayı Kapat' : 'Kamerayı Aç'}</span>
            </button>
            
            <button 
              className={`spm-control-button spm-run-button ${!isWebcamActive ? 'spm-button-disabled' : ''} ${isPredicting ? 'spm-running' : ''}`}
              onClick={runProject}
              disabled={!isWebcamActive}
            >
              <FaPlay className="spm-button-icon" />
              <span className="spm-button-text">{isPredicting ? 'Çalışıyor...' : 'Projeyi Çalıştır'}</span>
            </button>

            <div className={`spm-status-indicator ${isPredicting ? 'spm-status-active' : ''}`}>
              <FaEye className="spm-status-icon" />
              <span className="spm-status-text">{isPredicting ? 'İzleme Aktif' : 'İzleme Beklemede'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProjectMonitor;